// Măsurarea contactelor către vânzători (reguli v2).
//
// Reguli:
// - Vizualizare: o singură înregistrare per anunț per tab, cât timp pagina nu
//   e reîncărcată. Navigarea între anunțuri în același tab nu dublează.
//   Reîncărcarea paginii (F5) sau un tab nou înregistrează o vizualizare nouă.
//   Numărul agregat de vizualizări nu cere consimțământ: fără consimțământ se
//   salvează doar anunțul și data, fără ID de sesiune și fără sursă. Sesiunea,
//   sursa vizitei și evenimentul GA rămân doar cu consimțământ de analiză.
// - Clic telefon / WhatsApp = intenție de contact. Fiecare clic se numără;
//   doar repetările pe același canal + același loc + aceeași țintă (anunț sau
//   dealer) în mai puțin de 3 secunde sunt ignorate (dublu-tap). După 3 s un
//   clic nou se înregistrează. Valoarea NU înseamnă utilizatori sau leaduri unice.
// - Clic pe pagina dealerului: se leagă de dealer, fără anunț asociat.
// - Cerere trimisă = numai după confirmarea serverului (rândul din
//   listing_reservations e sursa de adevăr în dashboard).
// - În analytics (GA4) nu trimitem nume, telefon, email sau mesaj. Doar
//   ID anunț, tip vânzător, locul butonului și sursa vizitei.
// - GA4 primește evenimente numai cu consimțământ de analiză. Statisticile
//   interne pentru vânzători folosesc un ID de sesiune salvat în browser
//   numai cu consimțământ; altfel un ID temporar, ținut doar în memorie.
import { supabase } from '@/integrations/supabase/client';
import { trackEvent } from '@/lib/posthog';

export type SellerKind = 'dealer' | 'private' | 'unknown';
export type ContactChannel = 'phone' | 'whatsapp';
export type Placement = 'seller_card' | 'mobile_bar' | 'listing_card' | 'list_card' | 'quick_view' | 'dealer_page' | 'dealer_page_fix';

export interface TrackedListing {
  id: string;
  sellerId?: string | null;
  sellerType?: string | null;
}

const hasAnalyticsConsent = (): boolean => {
  try {
    return (
      localStorage.getItem('gdpr_banner_version') === 'v2.3' &&
      localStorage.getItem('gdpr_tracking_consent') === 'true'
    );
  } catch {
    return false;
  }
};

let memorySessionId: string | null = null;
const sessionId = (): string => {
  if (hasAnalyticsConsent()) {
    try {
      const key = 'roa_session_id';
      const re = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      let id = sessionStorage.getItem(key);
      if (!id || !re.test(id)) {
        id = crypto.randomUUID();
        sessionStorage.setItem(key, id);
      }
      return id;
    } catch {
      /* fall through */
    }
  }
  // Consimțământ retras: ștergem ID-ul salvat anterior.
  try { sessionStorage.removeItem('roa_session_id'); } catch { /* ignore */ }
  if (!memorySessionId) memorySessionId = crypto.randomUUID();
  return memorySessionId;
};

// Sursa vizitei, citită o singură dată la intrarea pe site (fără stocare).
let visitSource: string | null = null;
const getVisitSource = (): string => {
  if (visitSource) return visitSource;
  try {
    const p = new URLSearchParams(window.location.search);
    const utm = p.get('utm_source');
    if (utm) return (visitSource = utm.toLowerCase().slice(0, 40));
    if (p.get('gclid')) return (visitSource = 'google_ads');
    if (p.get('fbclid')) return (visitSource = 'facebook');
    const ref = document.referrer ? new URL(document.referrer).hostname : '';
    if (!ref || ref === window.location.hostname) return (visitSource = 'direct');
    if (/google\./.test(ref)) return (visitSource = 'google');
    if (/bing\.|duckduckgo\.|yahoo\./.test(ref)) return (visitSource = 'search_other');
    if (/facebook\.|instagram\.|fb\.|t\.co|tiktok\./.test(ref)) return (visitSource = 'social');
    if (/autocompare/.test(ref)) return (visitSource = 'autocompare');
    return (visitSource = 'referral');
  } catch {
    return (visitSource = 'unknown');
  }
};
if (typeof window !== 'undefined') getVisitSource();

// Clicks usually open the dialer / WhatsApp right away, so the request must start
// synchronously and survive the page switching apps (keepalive).
const postEvent = (body: Record<string, unknown>) => {
  try {
    const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/track-contact-event`;
    const key = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;
    void fetch(url, {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json', apikey: key, Authorization: `Bearer ${key}` },
      body: JSON.stringify(body),
    }).catch(() => {});
  } catch {
    /* ignore */
  }
};

const sellerKind = (t?: string | null): SellerKind =>
  t === 'private' ? 'private' : t ? 'dealer' : 'unknown';

const sendGa = (name: string, params: Record<string, string>) => {
  if (!hasAnalyticsConsent()) return;
  const g = (window as any).gtag;
  if (typeof g === 'function') g('event', name, params);
};

// ---- dedupe state (per tab) ----
const viewed = new Set<string>();
const lastClick = new Map<string, number>();
const submitted = new Set<string>();

export function trackListingView(l: TrackedListing): boolean {
  // Numărul agregat se înregistrează mereu (anonim, fără sesiune/sursă);
  // sesiunea, sursa și GA doar cu consimțământ de analiză.
  if (typeof window === 'undefined' || !l.id || viewed.has(l.id)) return false;
  viewed.add(l.id);
  if (!hasAnalyticsConsent()) {
    void supabase.functions
      .invoke('track-view', { body: { listingId: l.id } })
      .catch(() => {});
    return true;
  }
  const source = getVisitSource();
  void supabase.functions
    .invoke('track-view', { body: { listingId: l.id, sessionId: sessionId(), source } })
    .catch(() => {});
  sendGa('listing_view', { listing_id: l.id, seller_type: sellerKind(l.sellerType), traffic_source: source });
  return true;
}

export function trackContactClick(l: TrackedListing, channel: ContactChannel, placement: Placement): boolean {
  // Fără consimțământ nu măsurăm; butonul (tel:/WhatsApp) funcționează normal.
  if (typeof window === 'undefined' || !l.id || !hasAnalyticsConsent()) return false;
  const key = `l:${l.id}:${channel}:${placement}`;
  const now = Date.now();
  if (now - (lastClick.get(key) ?? 0) < 3000) return false;
  lastClick.set(key, now);
  const source = getVisitSource();
  const kind = sellerKind(l.sellerType);
  // Vânzătorul e stabilit pe server din anunț (inclusiv privați fără cont).
  postEvent({
        listingId: l.id,
        sessionId: sessionId(),
        eventType: channel === 'phone' ? 'call_click' : 'whatsapp_click',
        metadata: { channel, placement, seller_type: kind, source, target: 'seller', mv: '2' },
      });
  sendGa('seller_contact_click', {
    listing_id: l.id,
    channel,
    placement,
    seller_type: kind,
    traffic_source: source,
  });
  if (placement === 'seller_card' || placement === 'mobile_bar') {
    trackEvent(channel === 'phone' ? 'listing_call_click' : 'listing_whatsapp_click', { listing_id: l.id, seller_type: kind });
  }
  return true;
}

/** Opening a message is intent, not a saved seller request. */
export function trackListingMessageClick(l: TrackedListing): void {
  if (typeof window === 'undefined' || !l.id || !hasAnalyticsConsent()) return;
  trackEvent('listing_message_click', { listing_id: l.id, seller_type: sellerKind(l.sellerType) });
}

/** Clic pe telefonul / WhatsApp-ul dealerului din pagina dealerului (fără anunț). */
export function trackDealerContactClick(
  dealerProfileId: string | null | undefined,
  channel: ContactChannel,
  placement: Placement = 'dealer_page',
): boolean {
  if (typeof window === 'undefined' || !dealerProfileId || !hasAnalyticsConsent()) return false;
  const key = `d:${dealerProfileId}:${channel}:${placement}`;
  const now = Date.now();
  if (now - (lastClick.get(key) ?? 0) < 3000) return false;
  lastClick.set(key, now);
  const source = getVisitSource();
  postEvent({
        dealerProfileId,
        sessionId: sessionId(),
        eventType: channel === 'phone' ? 'call_click' : 'whatsapp_click',
        metadata: { channel, placement, seller_type: 'dealer', source, target: 'seller', mv: '2' },
      });
  sendGa('seller_contact_click', { channel, placement, seller_type: 'dealer', traffic_source: source, scope: 'dealer' });
  return true;
}

/** Call only after the server confirmed the request was saved. */
export function trackSellerRequestSubmitted(l: TrackedListing, requestId: string): boolean {
  if (typeof window === 'undefined' || !requestId || submitted.has(requestId)) return false;
  submitted.add(requestId);
  sendGa('seller_request_submitted', {
    listing_id: l.id,
    seller_type: sellerKind(l.sellerType),
    traffic_source: getVisitSource(),
  });
  return true;
}
